import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaUser,
  FaEnvelope,
  FaCity,
  FaPhone,
  FaMapMarkerAlt,
  FaIdCard,
  FaUserShield,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaSync,
  FaTint,
  FaBuilding,
} from "react-icons/fa";

const ReporterProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line
  }, []);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/icard`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setProfileData(response.data.data);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 text-center max-w-sm w-full">
          <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle className="w-6 h-6 text-amber-500" />
          </div>
          <h3 className="text-base font-black text-slate-900 mb-1">Profile Not Available</h3>
          <p className="text-sm text-slate-500 mb-5">Your profile appears after your ID card is verified.</p>
          <button
            onClick={fetchProfileData}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors"
          >
            <FaSync className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>
    );
  }

  const infoItems = [
    { icon: <FaEnvelope />, label: "Email", value: profileData?.email },
    { icon: <FaPhone />, label: "Phone", value: profileData?.mobileNo },
    { icon: <FaIdCard />, label: "IINSAF ID", value: profileData?.iinsafId, highlight: true },
    { icon: <FaUserShield />, label: "Designation", value: profileData?.designation },
    { icon: <FaTint />, label: "Blood Group", value: profileData?.bloodGroup },
    { icon: <FaMapMarkerAlt />, label: "Address", value: profileData?.ResidentialAddress },
    { icon: <FaCity />, label: "City", value: profileData?.city },
    { icon: <FaBuilding />, label: "State", value: profileData?.state },
    {
      icon: <FaCalendarAlt />,
      label: "Joined",
      value: profileData?.createdAt
        ? new Date(profileData.createdAt).toLocaleDateString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
        })
        : null,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Compact Profile Header */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-4 pt-6 pb-16 text-white">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white/20 shadow-xl flex-shrink-0">
            {profileData?.image ? (
              <img src={profileData.image} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-indigo-800 flex items-center justify-center">
                <FaUser className="w-7 h-7 text-indigo-300" />
              </div>
            )}
          </div>
          {/* Name & Role */}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-black text-white truncate">{profileData?.name || "Reporter"}</h1>
            <p className="text-indigo-300 text-xs font-bold uppercase tracking-widest truncate">
              {profileData?.designation || "IINSAF Member"}
            </p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[10px] font-bold text-white/60 uppercase tracking-widest">Verified Member</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - pulled up to overlap header */}
      <div className="max-w-2xl mx-auto px-3 -mt-10 pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">

          {/* IINSAF ID Badge */}
          {profileData?.iinsafId && (
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-3 flex items-center justify-between">
              <span className="text-[10px] font-black text-indigo-200 uppercase tracking-widest">IINSAF Press ID</span>
              <span className="text-sm font-black text-white tracking-tighter font-mono">{profileData.iinsafId}</span>
            </div>
          )}

          {/* Info Rows */}
          <div className="divide-y divide-slate-50">
            {infoItems.map((item, i) => (
              item.value && (
                <div key={i} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-500 text-xs">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">{item.label}</p>
                    <p className={`text-sm font-bold truncate ${item.highlight ? "text-indigo-600" : "text-slate-900"}`}>
                      {item.value}
                    </p>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Channel Section */}
          {profileData?.channelName && (
            <div className="border-t border-slate-100 px-5 py-4">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Official Channel</p>
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                {profileData?.channelLogo && (
                  <img
                    src={profileData.channelLogo}
                    alt="Channel"
                    className="w-10 h-10 rounded-xl object-cover border-2 border-white shadow"
                  />
                )}
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Channel Name</p>
                  <p className="text-sm font-black text-slate-900">{profileData.channelName}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/60 flex items-center justify-between">
            <p className="text-[9px] text-slate-400 uppercase tracking-widest font-bold">IINSAF Verified Reporter</p>
            <button
              onClick={fetchProfileData}
              className="flex items-center gap-1.5 text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-800 transition-colors"
            >
              <FaSync className="w-2.5 h-2.5" /> Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporterProfile;
